import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DEFAULT_PAGE_SIZE } from '../config/constants';
import { apiInstance } from '../utils/api';
import type { PaginatedResult, PaginationParams } from '../utils/types';

export const queryKeys = {
  base: ['leagueCategoriesBase'],
  categories: () => [...queryKeys.base, 'categories'],
  allCategories: () => [...queryKeys.base, 'allCategories'],
};

export type LeagueCategory = {
  id: number;
  name: string;
  is_primary: boolean;
};

export type ModifyLeagueCategoryPayload = {
  name: string;
  is_primary: boolean;
};

export const fetchLeagueCategories = async (params: PaginationParams) => {
  const response = await apiInstance.get<PaginatedResult<LeagueCategory>>('/api/league-category/paginated', {
    params,
  });

  return response.data;
};

export const useLeagueCategoriesQuery = () => {
  return useInfiniteQuery({
    queryKey: queryKeys.categories(),
    queryFn: ({ pageParam = 0 }) => fetchLeagueCategories({ size: DEFAULT_PAGE_SIZE, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages, lastPageParam) => {
      if (pages.length * DEFAULT_PAGE_SIZE > lastPage.itemsCount) {
        return undefined;
      }
      return lastPageParam + 1;
    },
  });
};

export const fetchAllLeagueCategories = async () => {
  const response = await apiInstance.get<LeagueCategory[]>('/api/league-category/all');
  return response.data;
};

export const useAllLeagueCategoriesQuery = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.allCategories(),
    queryFn: fetchAllLeagueCategories,
    staleTime: 20 * 60 * 1000,
    enabled: options?.enabled,
  });
};

export const createLeagueCategory = async (data: ModifyLeagueCategoryPayload) => {
  const res = await apiInstance.post<{ id: number }>('/api/league-category/', data);
  return res.data;
};

export const updateLeagueCategory = async (data: ModifyLeagueCategoryPayload & { id: number }) => {
  const res = await apiInstance.put<{ id: number }>(`/api/league-category/${data.id}`, data);
  return res.data;
};

export const deleteLeagueCategory = async ({ id }: { id: number }) => {
  const res = await apiInstance.delete<{}>(`/api/league-category/${id}`);
  return res.data;
};

const useInvalidatingMutation = <TVariables, TResult>(mutationFn: (variables: TVariables) => Promise<TResult>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.base });
    },
  });
};

export const useCreateLeagueCategoryMutation = () => useInvalidatingMutation(createLeagueCategory);
export const useUpdateLeagueCategoryMutation = () => useInvalidatingMutation(updateLeagueCategory);
export const useDeleteLeagueCategoryMutation = () => useInvalidatingMutation(deleteLeagueCategory);
